import { useAppDispatch, useAppSelector } from "./app/hooks";
import { RootState } from "./app/store";
import { Outlet, Route, Routes } from "react-router-dom";
import PrivateRoute from "./components/PrivateRoute";
import LoadingOverlay from "./components/LoadingOverlay";
import PublicRoute from "./components/PublicRoute";
import NotFound from "./pages/NotFound";
import SignIn from "./pages/Auth";
import Menu from "./components/Menu";
import {
	actions as commonActions,
	getGroups,
	getUser,
	readProfileImage,
} from "./app/commonSlice";

import { useCallback, useEffect, useState } from "react";
import { logOutUser, refreshSession } from "./pages/Auth/slice";
import {
	RESET_INFO,
	SET_IS_LOADING,
	SET_PROGRESS_VALUE,
} from "./app/actionTypes";
import Home from "./pages/Home";
import Profile from "./pages/Profile";
import Users from "./pages/Users";
import Groups from "./pages/Groups";
import Devices from "./pages/Devices";
import {
	createInterceptors,
	removeInterceptors,
} from "./services/interceptors";

function App() {
	const appDispatch = useAppDispatch();
	const { session }: any = useAppSelector(
		(state: RootState) => state.authReducer,
	);
	const { user, groups }: any = useAppSelector(
		(state: RootState) => state.commonReducer,
	);
	const { currentUser }: any = useAppSelector(
		(state: RootState) => state.profileReducer,
	);

	const [progressValue, setProgressValue] = useState(0);
	const [checkSession, setCheckSession] = useState(false);

	const signOut = useCallback(() => {
		appDispatch(commonActions[SET_IS_LOADING](true));
		appDispatch(commonActions[SET_PROGRESS_VALUE](1));
		appDispatch(logOutUser())
			.unwrap()
			.then(() => {
				appDispatch(commonActions[SET_IS_LOADING](false));
				appDispatch(commonActions[RESET_INFO]());
			})
			.catch(() => {
				appDispatch(commonActions[SET_IS_LOADING](false));
				appDispatch(commonActions[RESET_INFO]());
			});
	}, []);

	const handleValidateSession = useCallback(async () => {
		if (!checkSession) {
			setProgressValue(10);
			try {
				const response = await appDispatch(refreshSession(session)).unwrap();
				removeInterceptors();
				createInterceptors(
					{
						...response,
						refreshToken: response.refreshToken,
					},
					async (_session: any) =>
						await appDispatch(refreshSession(_session)).unwrap(),
				);
			} catch {
				removeInterceptors();
			}
			setCheckSession(true);
		} else {
			setProgressValue(30);
			await appDispatch(getUser()).unwrap();
			setProgressValue(60);
			await appDispatch(getGroups()).unwrap();
			setProgressValue(100);
		}
	}, [appDispatch, session, checkSession, refreshSession, getUser, getGroups]);

	useEffect(() => {
		if (session) {
			handleValidateSession();
		} else {
			setCheckSession(true);
			setProgressValue(100);
		}
	}, [session, checkSession]);

	const [refUser, setRefUser] = useState(currentUser || user);

	useEffect(() => {
		if (groups && user && (currentUser || user)) {
			const isAdmin = !!groups.find(
				(group: any) => group.name === "Administradores",
			);
			if (!isAdmin) {
				setRefUser(user);
			} else {
				setRefUser(currentUser || user);
			}
		}
	}, [currentUser, user, groups]);

	return (
		<Routes>
			<Route
				element={
					!checkSession || (session && (!user || !groups.length)) ? (
						<LoadingOverlay progressValue={progressValue || 80} />
					) : (
						<Outlet />
					)
				}
			>
				<Route
					element={
						<PrivateRoute authenticated={!!session} redirectRoute="/sign-in" />
					}
				>
					<Route
						element={
							<Menu
								readFileRequest={(imageKey) =>
									appDispatch(readProfileImage(imageKey)).unwrap()
								}
								user={refUser}
								signOut={signOut}
								userType={
									!groups.find((group: any) => group.name === "Administradores")
										? "common"
										: "administrator"
								}
							>
								<Outlet />
							</Menu>
						}
					>
						<Route path="/" element={<Home />} />
						<Route path="/home" element={<Home />} />
						<Route path="/profile" element={<Profile />} />
						<Route path="/users" element={<Users />} />
						<Route path="/groups" element={<Groups />} />
						<Route path="/devices" element={<Devices />} />
					</Route>
				</Route>
				<Route
					element={<PublicRoute authenticated={!!session} redirectRoute="/" />}
				>
					<Route path="/sign-in" element={<SignIn />} />
				</Route>
				<Route path="*" element={<NotFound />} />
			</Route>
		</Routes>
	);
}

export default App;
